import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Eye, EyeOff, Flag, Star, ChevronLeft, ChevronRight } from "lucide-react";

export default function ReviewsModerationTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const limit = 25;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/reviews?page=${page}&limit=${limit}${statusFilter ? `&status=${statusFilter}` : ""}`);
      setItems(r.data.reviews || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load reviews"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, statusFilter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/reviews/${id}/status`, { status });
      toast.success(`Review ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const lastPage = Math.max(1, Math.ceil(total / limit));

  return (
    <div data-testid="admin-reviews-moderation">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-[#1A231C] font-['Outfit']">Reviews Moderation</h2>
          <p className="text-sm text-[#8A998E]">{total} total · hide spam or abusive reviews from listings</p>
        </div>
        <div className="flex gap-2">
          {["", "visible", "hidden", "flagged"].map((s) => (
            <Button key={s || "all"} variant={statusFilter === s ? "default" : "outline"}
              size="sm" onClick={() => { setStatusFilter(s); setPage(1); }}
              data-testid={`reviews-filter-${s || "all"}`}>
              {s || "all"}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-sm text-[#8A998E]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-sm text-[#8A998E]">No reviews match this filter.</div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-[#E5E3DB] rounded-xl p-4" data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <strong className="text-sm">{r.customer_name || "Guest"}</strong>
                    <span className="text-yellow-600 text-sm tracking-wider">
                      {"★".repeat(r.rating)}<span className="text-[#E5E3DB]">{"★".repeat(5 - r.rating)}</span>
                    </span>
                    <Badge variant="outline" className="text-[10px]">{r.status || "visible"}</Badge>
                    <span className="text-[10px] text-[#8A998E]">{(r.created_at || "").slice(0, 10)}</span>
                  </div>
                  {r.comment && <p className="text-sm text-[#445049] my-1">{r.comment}</p>}
                  {(r.photos || []).length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {r.photos.map((p, i) => (
                        <a key={i} href={p} target="_blank" rel="noreferrer">
                          <img src={p} alt="" className="w-14 h-14 rounded object-cover border border-[#E5E3DB]" />
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="text-[11px] text-[#8A998E] mt-2">
                    Listing: <code>{r.listing_id}</code> · Booking: <code>{r.booking_id}</code>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {r.status !== "visible" && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "visible")}
                      data-testid={`review-approve-${r.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Show
                    </Button>
                  )}
                  {r.status !== "hidden" && (
                    <Button size="sm" variant="destructive" onClick={() => setStatus(r.id, "hidden")}
                      data-testid={`review-hide-${r.id}`}>
                      <EyeOff className="w-3.5 h-3.5 mr-1" /> Hide
                    </Button>
                  )}
                  {r.status !== "flagged" && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "flagged")}
                      data-testid={`review-flag-${r.id}`}>
                      <Flag className="w-3.5 h-3.5 mr-1" /> Flag
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Pagination */}
          {lastPage > 1 && (
            <div className="flex items-center justify-center gap-2 pt-3">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                data-testid="reviews-prev-page">
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </Button>
              <span className="text-sm text-[#445049]">Page {page} of {lastPage}</span>
              <Button variant="outline" size="sm" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}
                data-testid="reviews-next-page">
                Next <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
